import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, CSP_NONCE, ErrorHandler, NgModule } from '@angular/core';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import * as Sentry from '@sentry/angular';
import { ColorPickerModule } from 'ngx-color-picker';
import { provideEchartsCore } from 'ngx-echarts';
import { ImageCropperModule } from 'ngx-image-cropper';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { echarts } from './echarts-setup';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { environment } from 'src/environments/environment';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AccountsService } from 'src/services/accounts.service';
import { AIAutofillService } from 'src/services/ai-autofill.service';
import { AITesterService } from 'src/services/ai-tester.service';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { BoardsService } from 'src/services/boards.service';
import { CollaboratorTypesService } from 'src/services/collaborator-types.service';
import { CustomersService } from 'src/services/customers.service';
import { getEmbeddedAppConfig } from 'src/services/embedded-app-config.service';
import { EscalationsService } from 'src/services/escalations.service';
import { GlobalCreateService } from 'src/services/global-create.service';
import { GoogleApisService } from 'src/services/google-apis.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { KnowledgeBaseScrapeJobsService } from 'src/services/knowledge-base-scrape-jobs.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { ServerEventsService } from 'src/services/server-events.service';
import { SlackUserGroupsService } from 'src/services/slack-user-groups.service';
import { TagsService } from 'src/services/tags.service';
import { TenantsService } from 'src/services/tenants.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';
import { WebMcpService } from 'src/services/web-mcp.service';
import { WebhookSubscriptionsService } from 'src/services/webhook-subscriptions.service';

import { EmojisService } from '../services/emojis.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UnthreadTitleStrategy } from './app-title.strategy';
import { AddMemberModule } from './components/add-member/add-member.module';
import { AssistantSidebarComponent } from './components/assistant-sidebar/assistant-sidebar.component';
import { AvatarModule } from './components/avatar/avatar.module';
import { ComposePage } from './components/compose/compose.page';
import { CopilotPanelComponent } from './components/copilot-panel/copilot-panel.component';
import { CustomCommandBarModule } from './components/custom-command-bar/custom-command-bar.module';
import { CustomFieldsModule } from './components/custom-fields/custom-fields.module';
import { ResponseInputModule } from './components/custom-fields/response-input/response-input.module';
import { DateRangePickerModule } from './components/date-range-picker/date-range-picker.module';
import { DatetimePickerModule } from './components/datetime-picker/datetime-picker.module';
import { EditTagLinksModule } from './components/edit-tag-links/edit-tag-links.module';
import { EditTagModule } from './components/edit-tag/edit-tag.module';
import { FilterDropdownComponent } from './components/filter-dropdown/filter-dropdown.component';
import { ImageModalComponent } from './components/image-modal/image-modal.component';
import { MessagingModule } from './components/messaging/messaging.module';
import { SlackBlocksModule } from './components/slack-blocks/slack-blocks.module';
import { SlackMessageComponent } from './components/slack-message/slack-message.component';
import { SlackTextEditorComponent } from './components/slack-text-editor/slack-text-editor.component';
import { SupportStepsModule } from './components/support-steps/support-steps.module';
import { TimePickerModule } from './components/time-picker/time-picker.module';
import { UnlayerWrapperComponent } from './components/unlayer-wrapper/unlayer-wrapper.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { ProfilePopoverPage } from './profile/profile-popover/profile-popover.page';
import { init as initCustomFields } from './utils/custom-fields';
import { getAppConfig, getCspNonce, getFirebaseSettings } from './utils/utils';

const isEmbeddedApp = getEmbeddedAppConfig().enabled;

function getFirebaseAppName(): string | undefined {
  return getAppConfig().subdomain;
}

@NgModule({
  declarations: [
    AppComponent,
    ComposePage,
    ProfilePopoverPage,
    NotificationsComponent,
    ImageModalComponent,
    FilterDropdownComponent,
  ],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      innerHTMLTemplatesEnabled: true,
      rippleEffect: false,
    }),
    AppRoutingModule,
    FormsModule,
    NgxTippyModule,
    ImageCropperModule,
    CustomFieldsModule,
    SlackBlocksModule,
    SlackMessageComponent,
    DatetimePickerModule,
    DateRangePickerModule,
    ResponseInputModule,
    UnlayerWrapperComponent,
    SlackTextEditorComponent,
    AvatarModule,
    SharedPipesModule,
    MessagingModule,
    NgSelectModule,
    SharedDirectivesModule,
    EditTagLinksModule,
    EditTagModule,
    SupportStepsModule,
    AddMemberModule,
    TimePickerModule,
    BrowserAnimationsModule,
    ColorPickerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production && !isEmbeddedApp,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MonacoEditorModule.forRoot(),
    CustomCommandBarModule,
    AssistantSidebarComponent,
    CopilotPanelComponent,
  ],
  providers: [
    UploadService,
    AuthService,
    AITesterService,
    AIAutofillService,
    ApiService,
    ScreenTrackingService,
    UserTrackingService,
    AnalyticsService,
    MessageService,
    UsersService,
    TenantsService,
    CustomersService,
    TagsService,
    TicketTypesService,
    ProjectsService,
    CollaboratorTypesService,
    InboundEmailrulesService,
    SlackUserGroupsService,
    UserGroupsService,
    ServerEventsService,
    WebhookSubscriptionsService,
    KnowledgeBaseArticlesService,
    KnowledgeBaseScrapeJobsService,
    SearchablePopoverService,
    GlobalCreateService,
    GoogleApisService,
    EmojisService,
    AutomationsService,
    NangoService,
    AccountsService,
    BoardsService,
    EscalationsService,
    provideFirebaseApp(() => initializeApp(getFirebaseSettings(), getFirebaseAppName())),
    provideFirestore(() => {
      const firestore = getFirestore(getApp(getFirebaseAppName()));

      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 8082);
      }

      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth(getApp(getFirebaseAppName()));
      auth.tenantId = getAppConfig().tenantId ?? null;

      return auth;
    }),
    provideStorage(() => getStorage(getApp(getFirebaseAppName()))),
    provideAnalytics(() => getAnalytics(getApp(getFirebaseAppName()))),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    {
      provide: TitleStrategy,
      useExisting: UnthreadTitleStrategy,
    },
    {
      provide: APP_INITIALIZER,
      deps: [AuthService],
      useFactory: (authService: AuthService) => () => authService.initialization,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        // Start CEL initialization in the background without blocking app startup
        setTimeout(() => {
          initCustomFields().catch((err) => {
            console.error('Background CEL initialization failed:', err);
          });
        }, 100);

        // Return immediately resolved promise to avoid blocking app startup
        return () => Promise.resolve(true);
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      deps: [WebMcpService],
      useFactory: (webMcp: WebMcpService) => () => {
        webMcp.register();

        return Promise.resolve(true);
      },
      multi: true,
    },
    {
      provide: CSP_NONCE,
      useFactory: () => {
        return getCspNonce();
      },
    },
    provideEchartsCore({ echarts: () => Promise.resolve(echarts) }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
